import { useEffect, useState } from "react";
import { Activity, CheckCircle2, XCircle, Database, Zap, Shield, Server, Clock, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const Card = ({ title, icon: Icon, children, ok = true }) => (
  <div className={`bg-white/[0.02] border rounded-2xl p-5 ${ok ? "border-emerald-500/30" : "border-rose-500/40"}`}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        {Icon && <Icon size={16} className={ok ? "text-emerald-400" : "text-rose-400"} />}
        <h3 className="text-xs uppercase tracking-widest text-white/60">{title}</h3>
      </div>
      {ok ? <CheckCircle2 size={14} className="text-emerald-400" /> : <XCircle size={14} className="text-rose-400" />}
    </div>
    {children}
  </div>
);

const IntegrationBadge = ({ name, on }) => (
  <div className={`px-3 py-2 rounded-lg border text-xs flex items-center justify-between ${on ? "bg-emerald-500/10 border-emerald-500/30" : "bg-white/5 border-white/10 text-white/40"}`}>
    <span className="font-medium uppercase tracking-wider">{name}</span>
    <span className={`text-[10px] font-mono ${on ? "text-emerald-400" : "text-white/40"}`}>{on ? "● LIVE" : "○ off"}</span>
  </div>
);

export default function SystemHealth() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/ops/health");
      setHealth(r.data);
    } catch (e) { toast.error("Impossibile caricare health check"); }
    setLoading(false);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  if (!health) return <div className="text-white/60">Caricamento...</div>;

  return (
    <div className="space-y-6" data-testid="health-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight flex items-center gap-3">
            System Health <Activity className={health.ok ? "text-emerald-400" : "text-rose-400"} />
          </h1>
          <p className="text-sm text-white/45 mt-1">
            Avviato il {health.started_at?.slice(0, 19).replace("T", " ")} · Auto-refresh ogni 30s
          </p>
        </div>
        <button data-testid="health-refresh" onClick={load} disabled={loading}
          className="px-4 py-2 rounded-full text-xs bg-white/5 border border-white/10 hover:bg-white/10 inline-flex items-center gap-2">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Aggiorna
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card title="MongoDB" icon={Database} ok={health.checks?.mongodb?.status === "ok"}>
          <div className="text-2xl font-black">{health.checks?.mongodb?.status === "ok" ? "OK" : "ERROR"}</div>
          {health.checks?.mongodb?.error && <div className="text-[10px] text-rose-400 mt-1">{health.checks.mongodb.error}</div>}
        </Card>

        <Card title="Compliance Daemon 48h" icon={Shield} ok={!!health.checks?.compliance_daemon?.last_event}>
          <div className="text-xs text-white/60">Ultimo evento</div>
          <div className="text-sm font-mono mt-1">{health.checks?.compliance_daemon?.last_event?.slice(0,19).replace("T"," ") || "—"}</div>
        </Card>

        <Card title="Server Uptime" icon={Clock}>
          <div className="text-xs text-white/60">Started</div>
          <div className="text-sm font-mono mt-1">{health.started_at?.slice(0,19).replace("T"," ")}</div>
        </Card>
      </div>

      {/* Data volumes */}
      {health.checks?.data_volume && (
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Database size={14} className="text-[#ff0033]" />
            <h3 className="text-xs uppercase tracking-widest text-white/60">Volume dati</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(health.checks.data_volume).map(([k, v]) => (
              <div key={k} data-testid={`vol-${k}`} className="bg-black/30 border border-white/5 rounded-xl px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-white/40">{k.replace(/_/g, " ")}</div>
                <div className="text-xl font-black mt-1 font-mono">{v?.toLocaleString() || 0}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Integrations */}
      {health.checks?.integrations && (
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Zap size={14} className="text-[#ff0033]" />
            <h3 className="text-xs uppercase tracking-widest text-white/60">Integrazioni esterne</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {Object.entries(health.checks.integrations).map(([k, v]) => (
              <IntegrationBadge key={k} name={k.replace(/_/g, " ")} on={v} />
            ))}
          </div>
        </div>
      )}

      <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-4">
        <div className="text-xs text-white/60">Endpoint: <span className="font-mono text-emerald-400">/api/ops/health</span></div>
        <div className="text-[10px] text-white/30 mt-1">Useful per monitoring esterno (Uptime Robot, BetterUptime, ecc.) — risposta è sempre 200 OK se la app è viva.</div>
      </div>
    </div>
  );
}
